import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Receipt, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Sales", "Purchase", "Credit Note", "Debit Note"];

const AccountsVoucherTypes = () => {
  const [types, setTypes] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "Sales", name: "", year: "" });
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");

  const fetch = useCallback(async () => {
    const r = await axios.get(`${API}/accounts/voucher-types`);
    setTypes(r.data);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/accounts/voucher-types/${editing.id}`, form);
        toast.success("Voucher type updated");
      } else {
        await axios.post(`${API}/accounts/voucher-types`, form);
        toast.success("Voucher type created");
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ category: "Sales", name: "", year: "" });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this voucher type?")) return;
    await axios.delete(`${API}/accounts/voucher-types/${id}`);
    toast.success("Deleted");
    fetch();
  };

  const filtered = types.filter(t => {
    if (filterCat !== "All" && t.category !== filterCat) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const categoryColor = (cat) => {
    const map = { "Sales": "text-green-400 bg-green-900/30", "Purchase": "text-blue-400 bg-blue-900/30", "Credit Note": "text-amber-400 bg-amber-900/30", "Debit Note": "text-red-400 bg-red-900/30" };
    return map[cat] || "text-gray-400 bg-gray-800";
  };

  return (
    <div className="space-y-4" data-testid="voucher-types-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><Receipt className="w-5 h-5" /> Voucher Types</h2>
          <p className="text-gray-400 text-sm">Year-wise voucher type management</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ category: "Sales", name: "", year: "" }); setShowDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700" data-testid="add-voucher-type-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Voucher Type
        </Button>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-1">
          {["All", ...CATEGORIES].map(cat => (
            <Button key={cat} size="sm" variant={filterCat === cat ? "default" : "outline"}
              onClick={() => setFilterCat(cat)}
              className={filterCat === cat ? "bg-blue-700 text-white" : "border-gray-600 text-gray-400 text-xs"}>
              {cat}
            </Button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9 w-48" />
        </div>
      </div>

      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Category</TableHead>
                <TableHead className="text-gray-400">Name</TableHead>
                <TableHead className="text-gray-400">Year</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-8">No voucher types. Click "Add Voucher Type".</TableCell></TableRow>
              ) : filtered.map(t => (
                <TableRow key={t.id} className="border-gray-800">
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${categoryColor(t.category)}`}>{t.category}</span></TableCell>
                  <TableCell className="text-white font-medium">{t.name}</TableCell>
                  <TableCell className="text-cyan-400 font-mono">{t.year || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2"
                      onClick={() => { setEditing(t); setForm({ category: t.category, name: t.name, year: t.year || "" }); setShowDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(t.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader><DialogTitle className="text-blue-400">{editing ? "Edit" : "Add"} Voucher Type</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})} disabled={!!editing}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-gray-300">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., GST Sales 25-26" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Year</Label><Input value={form.year} onChange={e => setForm({...form, year: e.target.value})} placeholder="e.g., 25-26" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-voucher-type-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsVoucherTypes;
